import React, { useState, useCallback, useEffect } from 'react';
import { ConfirmDialog, DialogContent, DialogContentText, Button } from '@hqo/react-components-library';
import styled from 'styled-components';
import { useIntl, FormattedMessage } from 'react-intl';
import { BuildingSelect } from 'components/app-instance-config/app-instance-config-form-content/app-instance-information-section/building-form-select/building-select';
import { useBuilding } from 'store/buildings/hooks';
import {
  useCreateAppInstanceConfigHandler,
  useCreateButtonLoading,
} from 'pages/create-app-instance-config-page/create-app-instance-config-page.hooks';
import { AppInstanceConfigFormValues } from 'components/app-instance-config/app-instance-config-form-values.interface';
import { Instance, InstanceOwnerType } from 'store/instances/types';
import { useSelector } from 'react-redux';
import { instancesState } from 'store/instances/selectors';

const DialogContentStyled = styled(DialogContent)`
  margin: 32px !important;
  min-width: 450px;
`;

const DialogForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 0;
`;

const DialogActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding: 16px 0 0 0;
`;

const Title = styled.div`
  font-style: normal;
  font-weight: 500;
  font-size: 24px;
  line-height: 28px;
  padding: 0;
  font-family: ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.greys.adminBlack};
`;

const Description = styled(DialogContentText)`
  color: ${({ theme }) => theme.colors.greys.adminGreyDark} !important;
  text-align: unset !important;
`;

const WarningText = styled.div`
  color: ${({ theme }) => theme.colors.admin.pink};
  font-style: italic;
  font-size: 14px;
`;

interface CopyInstanceModalProps {
  instance: Instance | null;
  onClose: () => void;
}

export const CopyInstanceModal = ({ instance, onClose }: CopyInstanceModalProps): JSX.Element => {
  const { formatMessage } = useIntl();
  const createAppInstanceConfig = useCreateAppInstanceConfigHandler();
  const isCreating = useCreateButtonLoading();
  const { createAppInstanceConfigState } = useSelector(instancesState);

  const [selectedBuildingUuid, setSelectedBuildingUuid] = useState<string | null>(null);
  const selectedBuilding = useBuilding(selectedBuildingUuid);

  const handleCancel = useCallback(() => {
    setSelectedBuildingUuid(null);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!isCreating && createAppInstanceConfigState.error && createAppInstanceConfigState.isCopy) {
      handleCancel();
    }
  }, [isCreating, createAppInstanceConfigState.error, createAppInstanceConfigState.isCopy, handleCancel]);

  const handleBuildingChange = useCallback((event: React.ChangeEvent<{ value: unknown }>) => {
    const buildingUuid = event.target.value as string;
    setSelectedBuildingUuid(buildingUuid);
  }, []);

  const isSameBuilding = selectedBuildingUuid === instance?.building?.uuid;
  const canCopy = selectedBuildingUuid && instance && !isSameBuilding;

  const handleCopyInstance = useCallback(() => {
    if (!canCopy || !instance || !selectedBuildingUuid) return;

    const appInstanceConfig: AppInstanceConfigFormValues = {
      app_config_uuid: instance.app_config_uuid,
      base_url: instance.base_url || null,
      enabled: instance.enabled ?? true,
      owner_type: InstanceOwnerType.BUILDING,
      owner_uuid: selectedBuildingUuid,
      config: instance.config,
      external_id: instance.external_id,
    };

    createAppInstanceConfig(appInstanceConfig, true);
  }, [canCopy, instance, selectedBuildingUuid, createAppInstanceConfig]);

  return (
    <ConfirmDialog onClose={handleCancel} open={!!instance}>
      <DialogContentStyled>
        <Title>{formatMessage({ id: 'copyInstanceDialog.title' })}</Title>
        <DialogForm>
          <Description>
            <FormattedMessage
              id="copyInstanceDialog.description"
              values={{
                appName: <strong>{instance?.app?.name}</strong>,
                buildingName: <strong>{instance?.building?.name}</strong>,
              }}
            />
          </Description>
          <BuildingSelect
            label={formatMessage({ id: 'copyInstanceDialog.buildingLabel' })}
            placeholder={formatMessage({ id: 'copyInstanceDialog.buildingPlaceholder' })}
            selectedBuilding={selectedBuilding}
            name="targetBuilding"
            onChange={handleBuildingChange}
            disabled={isCreating}
            dataCy="targetBuilding"
          />
          {isSameBuilding && (
            <WarningText>{formatMessage({ id: 'copyInstanceDialog.sameBuildingWarning' })}</WarningText>
          )}
        </DialogForm>
        <DialogActions>
          <Button
            variant="cancelButton"
            onClick={handleCancel}
            disabled={isCreating}
            dataCy="copy-instance-cancel"
            text={formatMessage({ id: 'buttons.cancel' })}
          />
          <Button
            variant="primary"
            onClick={handleCopyInstance}
            disabled={!canCopy || isCreating}
            loading={isCreating}
            dataCy="copy-instance-confirm"
            text={formatMessage({ id: 'buttons.copyInstance' })}
          />
        </DialogActions>
      </DialogContentStyled>
    </ConfirmDialog>
  );
};
